import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/translations";
import { Reveal } from "./Reveal";

const GoogleG = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);

export const Reviews = ({ content, reviews }) => {
  const { lang } = useLang();
  if (!reviews || reviews.length === 0) return null;

  return (
    <section id="reviews" className="relative z-10 bg-[#0A0A0A] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF5A00]">{t("reviews_kicker", lang)}</span>
          <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-none">{t("reviews_title", lang)}</h2>
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#121212] px-5 py-4">
              <GoogleG />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-3xl leading-none">{content.rating}</span>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={15} className="fill-[#FBBC05] text-[#FBBC05]" />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-neutral-400 mt-1">
                  {content.review_count} {t("reviews_on_google", lang)}
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {reviews.map((r, i) => (
            <motion.article
              key={r.id}
              data-testid={`review-${r.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
              className="group rounded-2xl border border-white/5 bg-[#121212] p-6 hover:border-[#FF5A00]/40 transition-colors"
            >
              <Quote size={28} className="text-[#FF5A00]/40 mb-3" />
              <div className="flex gap-0.5 mb-3">
                {[...Array(r.rating)].map((_, s) => (
                  <Star key={s} size={14} className="fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </div>
              <p className="text-neutral-200 leading-relaxed">{lang === "es" ? r.text_es : r.text_en}</p>
              <div className="mt-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FF5A00]/15 text-[#FF5A00] font-bold text-sm">
                  {r.author?.[0] || "?"}
                </span>
                <div>
                  <p className="font-semibold text-sm leading-none">{r.author}</p>
                  <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1">
                    <GoogleG /> {r.date}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
